import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../AuthContext';
import { LuPackage, LuCreditCard, LuX } from 'react-icons/lu';

const TYPE_META = {
  traffic: { title: 'Трафик' },
  devices: { title: 'Устройства' },
  squad: { title: 'Сквады' }
};

const PROVIDER_DISPLAY_NAMES = {
  balance: 'Оплатить с баланса',
  crystalpay: 'CrystalPay',
  heleket: 'Heleket (Криптовалюты)',
  yookassa: 'YooKassa (RUB)',
  yoomoney: 'YooMoney (RUB)',
  platega: 'Platega',
  platega_mir: 'Карты МИР',
  mulenpay: 'Mulenpay',
  urlpay: 'UrlPay',
  telegram_stars: 'Telegram Stars ⭐',
  monobank: 'Monobank 💳',
  btcpayserver: 'BTCPayServer ₿ (Bitcoin)',
  tribute: 'Tribute',
  robokassa: 'Robokassa',
  freekassa: 'Freekassa',
  kassa_ai: 'Kassa AI',
  cryptobot: 'CryptoBot'
};

function currencySymbol(code) {
  const c = String(code || '').toLowerCase();
  if (c === 'uah') return '₴';
  if (c === 'rub') return '₽';
  if (c === 'usd') return '$';
  return '';
}

function optionPriceForCurrency(option, currency) {
  const c = String(currency || '').toLowerCase();
  if (c === 'uah') return Number(option.price_uah || 0);
  if (c === 'rub') return Number(option.price_rub || 0);
  if (c === 'usd') return Number(option.price_usd || 0);
  return Number(option.price_rub || 0);
}

export default function ClientOptionsPage() {
  const { token, currency, user } = useAuth();
  const effectiveCurrency = currency || user?.preferred_currency || 'rub';

  const [optionsByType, setOptionsByType] = useState({ traffic: [], devices: [], squad: [] });
  const [paymentMethods, setPaymentMethods] = useState([]); // array of provider strings
  const [configs, setConfigs] = useState([]);
  const [selectedConfigId, setSelectedConfigId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedOption, setSelectedOption] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [creatingPayment, setCreatingPayment] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [optsRes, pmRes] = await Promise.all([
          fetch('/api/public/purchase-options', { cache: 'no-store' }),
          fetch('/api/public/available-payment-methods')
        ]);

        if (optsRes.ok) {
          const data = await optsRes.json();
          const grouped = data?.options || {};
          setOptionsByType({
            traffic: Array.isArray(grouped.traffic) ? grouped.traffic : [],
            devices: Array.isArray(grouped.devices) ? grouped.devices : [],
            squad: Array.isArray(grouped.squad) ? grouped.squad : []
          });
        } else {
          setOptionsByType({ traffic: [], devices: [], squad: [] });
        }

        if (pmRes.ok) {
          const pm = await pmRes.json();
          setPaymentMethods(Array.isArray(pm?.available_methods) ? pm.available_methods : []);
        } else {
          setPaymentMethods([]);
        }
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  useEffect(() => {
    const loadConfigs = async () => {
      if (!token) return;
      try {
        const res = await fetch('/api/client/configs', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data?.configs) ? data.configs : [];
          setConfigs(list);
          const primary = list.find(c => c.is_primary);
          setSelectedConfigId(primary?.id || list?.[0]?.id || null);
        }
      } catch (_) {
        // optional
      }
    };
    loadConfigs();
  }, [token]);

  const totalCount = useMemo(() => (
    (optionsByType.traffic?.length || 0) +
    (optionsByType.devices?.length || 0) +
    (optionsByType.squad?.length || 0)
  ), [optionsByType]);

  const openPayment = (opt) => {
    setSelectedOption(opt);
    setShowPaymentModal(true);
  };

  const createPayment = async (paymentProvider) => {
    if (!token || !selectedOption) return;
    setCreatingPayment(true);
    setError(null);
    try {
      const res = await fetch('/api/client/create-option-payment', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          option_id: selectedOption.id,
          payment_provider: paymentProvider,
          config_id: selectedConfigId
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Ошибка создания платежа');
      if (paymentProvider === 'balance' && data.success) {
        setShowPaymentModal(false);
        setSelectedOption(null);
        window.location.reload();
        return;
      }
      if (data.payment_url) {
        window.location.href = data.payment_url;
      } else {
        throw new Error('Не удалось получить ссылку оплаты');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setCreatingPayment(false);
      setShowPaymentModal(false);
    }
  };

  return (
    <main className="client-page-content">
      <section>
        <h2 className="admin-page-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <LuPackage /> Опции
        </h2>
        <p className="admin-description-text" style={{ marginBottom: 16 }}>
          Дополнительные покупки: докупить трафик / устройства / сквад. Доступно опций: {totalCount}.
        </p>

        {error && <div className="message-error">{error}</div>}

        {configs.length > 0 && (
          <div style={{ marginBottom: 16, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Применить к конфигу:</div>
            <select
              className="lang-select"
              value={selectedConfigId || ''}
              onChange={(e) => setSelectedConfigId(e.target.value ? Number(e.target.value) : null)}
              style={{ minWidth: 220 }}
            >
              {configs.map(c => (
                <option key={c.id} value={c.id}>
                  {c.name || c.remnawave_uuid || `Config #${c.id}`}{c.is_primary ? ' (primary)' : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        {loading ? (
          <div className="loading-full">Загрузка...</div>
        ) : (
          <div style={{ display: 'grid', gap: 18 }}>
            {Object.keys(TYPE_META).map((type) => {
              const list = optionsByType[type] || [];
              return (
                <div key={type} style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: 12, padding: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <div style={{ fontWeight: 800, color: 'var(--text-primary)' }}>{TYPE_META[type].title}</div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{list.length}</div>
                  </div>

                  {list.length === 0 ? (
                    <div style={{ color: 'var(--text-secondary)' }}>Нет доступных опций</div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
                      {list.map((opt) => {
                        const price = optionPriceForCurrency(opt, effectiveCurrency);
                        return (
                          <div key={opt.id} style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: 12, padding: 14 }}>
                            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 6 }}>
                              <div style={{ width: 36, height: 36, borderRadius: 12, background: 'var(--bg-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--border-color)', fontSize: 18 }}>
                                {opt.icon || '📦'}
                              </div>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opt.name}</div>
                                <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                                  {opt.value}{opt.unit ? ` ${opt.unit}` : ''}
                                </div>
                              </div>
                            </div>

                            {opt.description ? (
                              <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 10, minHeight: 34, overflow: 'hidden' }}>
                                {opt.description}
                              </div>
                            ) : (
                              <div style={{ minHeight: 34 }} />
                            )}

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                              <div style={{ fontWeight: 900 }}>
                                {currencySymbol(effectiveCurrency)} {price.toFixed(2)}
                              </div>
                              <button className="btn" onClick={() => openPayment(opt)} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                <LuCreditCard /> Купить
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {showPaymentModal && selectedOption && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 18,
            zIndex: 1000
          }}
          onClick={() => !creatingPayment && setShowPaymentModal(false)}
        >
          <div
            style={{
              width: 'min(560px, 100%)',
              background: 'var(--bg-primary)',
              border: '1px solid var(--border-color)',
              borderRadius: 14,
              padding: 16
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ fontWeight: 900 }}>Оплата: {selectedOption.name}</div>
              <button className="btn-icon" onClick={() => !creatingPayment && setShowPaymentModal(false)} aria-label="Close">
                <LuX />
              </button>
            </div>

            <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 12 }}>
              Выберите способ оплаты.
            </div>

            <div style={{ display: 'grid', gap: 10 }}>
              {paymentMethods.length === 0 ? (
                <div style={{ color: 'var(--text-secondary)' }}>Методы оплаты не найдены</div>
              ) : (
                paymentMethods.map((provider) => (
                  <button
                    key={provider}
                    className="btn"
                    disabled={creatingPayment}
                    onClick={() => createPayment(provider)}
                    style={{ justifyContent: 'space-between', display: 'flex', width: '100%' }}
                  >
                    <span>{PROVIDER_DISPLAY_NAMES[provider] || provider}</span>
                    <span style={{ color: 'var(--text-secondary)', fontWeight: 700 }}>{creatingPayment ? '...' : '→'}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

